import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Shield, ClipboardList, BookOpen, Layers, ScrollText, Video, Settings, ArrowRight, RefreshCw } from "lucide-react";
import AdminQuestionsEditor from "@/components/admin/AdminQuestionsEditor";
import AdminLessonsEditor from "@/components/admin/AdminLessonsEditor";
import AdminModulesEditor from "@/components/admin/AdminModulesEditor";
import AdminFlashcardsEditor from "@/components/admin/AdminFlashcardsEditor";

const TABS = [
  { key: "overview", label: "Overview", icon: Shield },
  { key: "questions", label: "Test Questions", icon: ClipboardList },
  { key: "lessons", label: "Lessons & Content", icon: BookOpen },
  { key: "flashcards", label: "Flashcards", icon: Layers },
  { key: "modules", label: "Modules", icon: Layers },
];

export default function AdminPage() {
  const [params, setParams] = useSearchParams();
  const active = params.get("tab") || "overview";
  const [overview, setOverview] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  async function loadOverview() {
    setRefreshing(true);
    try {
      const { data } = await api.get("/admin/overview");
      setOverview(data);
    } catch (e) { /* noop */ }
    setRefreshing(false);
  }

  useEffect(() => { if (active === "overview") loadOverview(); }, [active]);

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-page">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-sm border border-[var(--dw-gold)]/60 bg-[var(--dw-gold)]/10 flex items-center justify-center">
            <Shield className="w-5 h-5 text-[var(--dw-gold-soft)]" strokeWidth={1.5} />
          </div>
          <div className="overline text-[var(--dw-gold-soft)]">Admin Console</div>
        </div>
        <h1 className="font-serif text-4xl md:text-5xl text-[var(--dw-navy)] mb-2">Content Manager</h1>
        <p className="text-neutral-600 mb-8 max-w-2xl">Edit any question, lesson, or module across the site. Your changes are stored as overrides — the seed content is preserved, and you can reset anything back to default with one click.</p>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 border-b border-[var(--dw-navy)]/10 mb-8">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = active === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setParams({ tab: t.key })}
                data-testid={`admin-tab-${t.key}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${
                  isActive
                    ? "border-[var(--dw-gold)] text-[var(--dw-navy)]"
                    : "border-transparent text-neutral-500 hover:text-[var(--dw-navy)]"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                {t.label}
              </button>
            );
          })}
        </div>

        {active === "overview" && (
          <div data-testid="admin-overview">
            <div className="flex items-center justify-between mb-5">
              <div className="font-serif text-2xl text-[var(--dw-navy)]">Content at a glance</div>
              <button onClick={loadOverview} disabled={refreshing} className="text-xs text-neutral-500 hover:text-[var(--dw-navy)] inline-flex items-center gap-1.5 disabled:opacity-50">
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.5} /> Refresh
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <OverviewStat n={overview?.modules ?? "—"} l="Modules" />
              <OverviewStat n={overview?.lessons ?? "—"} l="Lessons" />
              <OverviewStat n={overview?.questions ?? "—"} l="Module Qs" />
              <OverviewStat n={overview?.sample_questions ?? "—"} l="Sample Qs" />
              <OverviewStat n={overview?.flashcards ?? "—"} l="Flashcards" />
              <OverviewStat n={overview?.users ?? "—"} l="Users" />
              <OverviewStat n={overview?.attempts ?? "—"} l="Quiz Attempts" />
              <OverviewStat n={overview?.total_edits ?? "—"} l="Live Overrides" highlight />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <QuickLink onClick={() => setParams({ tab: "questions" })} icon={ClipboardList} title="Edit Test Questions" sub="188 module Qs + 10 sample Qs. Fix wording, choices, or answer keys." />
              <QuickLink onClick={() => setParams({ tab: "lessons" })} icon={BookOpen} title="Edit Lessons" sub="33 lessons across 5 modules. Update titles, markdown content, or duration." />
              <QuickLink onClick={() => setParams({ tab: "flashcards" })} icon={Layers} title="Edit Flashcards" sub="678 cards across 5 categories. Add, edit, bulk import from paste." />
              <QuickLink onClick={() => setParams({ tab: "modules" })} icon={Layers} title="Edit Modules" sub="Rename modules, update descriptions, adjust exam weight." />
            </div>
          </div>
        )}

        {active === "questions" && <AdminQuestionsEditor />}
        {active === "lessons" && <AdminLessonsEditor />}
        {active === "flashcards" && <AdminFlashcardsEditor />}
        {active === "modules" && <AdminModulesEditor />}
      </div>
    </AppLayout>
  );
}

function OverviewStat({ n, l, highlight }) {
  return (
    <div className={`border rounded-sm p-4 ${highlight ? "border-[var(--dw-gold)]/50 bg-[var(--dw-gold)]/5" : "border-[var(--dw-navy)]/12 bg-white"}`}>
      <div className={`font-serif text-3xl leading-none ${highlight ? "text-[var(--dw-gold-soft)]" : "text-[var(--dw-navy)]"}`}>{n}</div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-2">{l}</div>
    </div>
  );
}

function QuickLink({ onClick, icon: Icon, title, sub }) {
  return (
    <button onClick={onClick} className="dw-card group flex items-center gap-4 p-5 text-left hover:border-[var(--dw-gold)]">
      <div className="w-10 h-10 border border-[var(--dw-navy)]/12 flex items-center justify-center rounded-sm shrink-0">
        <Icon className="w-4 h-4 text-[var(--dw-navy)]" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-serif text-lg text-[var(--dw-navy)] leading-tight">{title}</div>
        <div className="text-xs text-neutral-600 mt-1">{sub}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-neutral-400 group-hover:text-[var(--dw-navy)] group-hover:translate-x-1 transition-all shrink-0" strokeWidth={1.5} />
    </button>
  );
}
